import { COLORS } from "@msuite/utils/colors";
import clsx from "clsx";
import { motion } from "motion/react";
import { type Control, Controller, type FieldValues, type Path } from "react-hook-form";
import { useTranslation } from "../localization";
import { Tooltip } from "../tooltip";
import { type ExtendedInputWrapperProps, InputWrapper } from "./input-wrapper";

/** Props Interface */
interface ColorPickerProps<T extends FieldValues> extends ExtendedInputWrapperProps<T> {
	control: Control<T>;
	name: Path<T>;
	"data-cy"?: string;
}

export function ColorPicker<T extends FieldValues>({
	control,
	name,
	isDisabled,
	"data-cy": dataCy,
	...props
}: ColorPickerProps<T>) {
	/** Hooks */
	const { t } = useTranslation();

	/** Render */
	return (
		<Controller
			name={name}
			control={control}
			render={({ field, formState }) => (
				<InputWrapper
					{...props}
					name={name}
					errors={formState.errors}
				>
					<div
						className="gap-2 grid w-full"
						style={{
							gridTemplateColumns: "repeat(auto-fill, minmax(1.5rem, 1fr))",
							gridAutoRows: "max-content",
						}}
						data-cy={dataCy}
					>
						{COLORS.map((color) => {
							/** Computed */
							const isSelected = field.value === color;
							if (color === "primary") return null;

							/** Render */
							return (
								<Tooltip
									label={t(`theme.${color}`)}
									key={color}
									openDelay={250}
								>
									<div
										onClick={() => {
											if (isDisabled) return;
											field.onChange(color);
										}}
										onKeyUp={() => {}}
										className={clsx(
											"rounded-full w-full aspect-square",
											"flex-center",
											"cursor-pointer",
											"p-1.5",
											isDisabled && "opacity-50 cursor-not-allowed",
										)}
										data-cy="color-option"
										style={{
											backgroundColor: `var(--color-${color}-500)`,
										}}
										key={color}
									>
										{isSelected && (
											<motion.div
												className="bg-white rounded-full w-full aspect-square"
												transition={{ ease: "circInOut", duration: 0.2 }}
											/>
										)}
									</div>
								</Tooltip>
							);
						})}
					</div>
				</InputWrapper>
			)}
		/>
	);
}
